import React, { useState, useEffect, useCallback } from 'react';
import {
  ScrollText, Search, Filter, RefreshCw, Activity, Clock, ChevronLeft, ChevronRight,
  Plus, Pencil, Trash2, LogIn, LogOut, Download, Send, CreditCard, ArrowRightLeft, X
} from 'lucide-react';
import { LoadingSpinner, ErrorMessage } from '../../UI';
import { auditAPI } from '../../../services';
import { FONT_UI, PageHeader, GhostButton } from './theme';

const ACTIONS = [
  { value: 'create', label: 'Create', color: 'bg-green-100 text-green-800', icon: Plus },
  { value: 'update', label: 'Update', color: 'bg-blue-100 text-blue-800', icon: Pencil },
  { value: 'delete', label: 'Delete', color: 'bg-red-100 text-red-800', icon: Trash2 },
  { value: 'payment', label: 'Payment', color: 'bg-emerald-100 text-emerald-800', icon: CreditCard },
  { value: 'status_change', label: 'Status Change', color: 'bg-purple-100 text-purple-800', icon: ArrowRightLeft },
  { value: 'login', label: 'Login', color: 'bg-gray-100 text-gray-700', icon: LogIn },
  { value: 'logout', label: 'Logout', color: 'bg-gray-100 text-gray-700', icon: LogOut },
  { value: 'export', label: 'Export', color: 'bg-amber-100 text-amber-800', icon: Download },
  { value: 'send', label: 'Send', color: 'bg-indigo-100 text-indigo-800', icon: Send },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700', icon: Activity }
];

const actionMeta = (v) => ACTIONS.find((a) => a.value === v) || ACTIONS[ACTIONS.length - 1];

const formatTimestamp = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '-';

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ modules: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({ search: '', action: '', module: '', actor: '', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const response = await auditAPI.getLogs(params);
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, filters, pagination.limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    auditAPI
      .getStats()
      .then((res) => setStats(res.data))
      .catch((err) => console.error('Failed to load audit stats:', err.message));
    auditAPI
      .getFilters()
      .then((res) => setOptions(res.data))
      .catch((err) => console.error('Failed to load audit filters:', err.message));
  }, []);

  const resetFilters = () => {
    setPage(1);
    setFilters({ search: '', action: '', module: '', actor: '', startDate: '', endDate: '' });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Audit Log"
        subtitle="Append-only trail of every action taken in the superadmin panel"
        actions={<GhostButton icon={RefreshCw} onClick={fetchLogs}>Refresh</GhostButton>}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} className="mb-4" />}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <ScrollText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Entries</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Last 24 Hours</p>
                <p className="text-2xl font-bold text-blue-600">{stats.last24h}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500 mb-2">Top Actions</p>
            <div className="space-y-1">
              {(stats.byAction || []).slice(0, 3).map((a) => (
                <div key={a._id} className="flex justify-between text-sm">
                  <span className="text-gray-700">{actionMeta(a._id).label}</span>
                  <span className="font-medium text-gray-900">{a.count}</span>
                </div>
              ))}
              {(stats.byAction || []).length === 0 && <p className="text-sm text-gray-400">No activity</p>}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500 mb-2">Top Modules</p>
            <div className="space-y-1">
              {(stats.byModule || []).slice(0, 3).map((m) => (
                <div key={m._id} className="flex justify-between text-sm">
                  <span className="text-gray-700 capitalize">{m._id || 'unknown'}</span>
                  <span className="font-medium text-gray-900">{m.count}</span>
                </div>
              ))}
              {(stats.byModule || []).length === 0 && <p className="text-sm text-gray-400">No activity</p>}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 mb-4 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, search: e.target.value });
              }}
              placeholder="Search description, record or user..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" /> Filters
          </button>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-200">
            <select
              value={filters.action}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, action: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All actions</option>
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
            <select
              value={filters.module}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, module: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All modules</option>
              {options.modules.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <select
              value={filters.actor}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, actor: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All users</option>
              {options.actors.map((a) => (
                <option key={a._id} value={a._id}>{a.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, startDate: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, endDate: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <ScrollText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No audit entries found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => {
                    const meta = actionMeta(log.action);
                    const ActionIcon = meta.icon;
                    return (
                      <tr key={log._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetail(log)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatTimestamp(log.createdAt)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">{log.actor?.name || log.actorName || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{log.actorRole}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
                            <ActionIcon className="w-3 h-3" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">{log.module || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">{log.description || log.entityLabel || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages} &middot; {pagination.total} entries
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Entry detail - includes the raw before/after payload where one was recorded */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">Audit Entry</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500">Timestamp</p>
                  <p className="font-medium text-gray-900">{formatTimestamp(detail.createdAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">User</p>
                  <p className="font-medium text-gray-900">
                    {detail.actor?.name || detail.actorName || 'Unknown'} ({detail.actorRole})
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Action</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionMeta(detail.action).color}`}>
                    {actionMeta(detail.action).label}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Module</p>
                  <p className="font-medium text-gray-900 capitalize">{detail.module || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Record</p>
                  <p className="font-medium text-gray-900">{detail.entityLabel || '-'}</p>
                  <p className="text-xs text-gray-400">{detail.entityType}</p>
                </div>
                <div>
                  <p className="text-gray-500">IP Address</p>
                  <p className="font-medium text-gray-900">{detail.ip || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-gray-500 mb-1">Description</p>
                <p className="text-gray-900">{detail.description || '-'}</p>
              </div>

              {detail.changes && (
                <div>
                  <p className="text-gray-500 mb-1">Changes</p>
                  <pre className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 overflow-x-auto">
                    {JSON.stringify(detail.changes, null, 2)}
                  </pre>
                </div>
              )}

              {detail.userAgent && (
                <div>
                  <p className="text-gray-500 mb-1">User Agent</p>
                  <p className="text-xs text-gray-600 break-all">{detail.userAgent}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
